'use strict';

import omniclone from 'omniclone';

import {
    PANELMANAGER_OPEN_PANEL,
    PANELMANAGER_OPEN_POPUP,
    PANELMANAGER_OPEN_PANEL_DENIED
} from '../../constants';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributes,
    itemAttributes,
    ItemOnlyAttributesForCreation
} from '~/shared/src/ci/Item';
import {Condition} from '~/shared/src/utilities/condition';
import UserTemplateStringResolver from '~/shared/src/utilities/UserTemplateStringResolver';

import setPanelPropsValidationError, {
    SetPanelPropsValidationErrorAction
} from '~/frontend/src/actions/panelManager/setPanelPropsValidationError';
import {
    panelClasses,
    PanelType,
    PanelClass,
    PanelPropsByPanelType
} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {
    preValidatePanelProps,
    validatePanelProps,
    preInitializePanelProps,
    initializePanelProps
} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import keyboardUtils from '~/frontend/src/utilities/keyboardUtils';
import {resolveIconClass} from '~/frontend/src/utilities/panelInfo';
import {resolveConditionRecursive} from '~/frontend/src/utilities/dataUtils';
import messageUtils from '~/shared/src/utilities/messageUtils';


export type ProcessFinalPanelProps = ( panelProps : PanelPropsByPanelType ) => PanelPropsByPanelType;
export type OpenPanelAction = {
    type : typeof PANELMANAGER_OPEN_PANEL;
    panelProps : PanelPropsByPanelType;
    processFinalPanelProps? : ProcessFinalPanelProps;
    inBackground : boolean;
    panelIndex? : number;
    dontClearData? : boolean;
    path? : string;
} | {
    type : typeof PANELMANAGER_OPEN_POPUP;
    panelProps : PanelPropsByPanelType;
    processFinalPanelProps? : ProcessFinalPanelProps;
    isBlocking : boolean;
    saveOnOk : boolean;
    dontClearData? : boolean;
    panelIndex? : never;
    path? : string;
};

type DenyOpenPanelAction = {
    type : typeof PANELMANAGER_OPEN_PANEL_DENIED;
    panelProps : PanelPropsByPanelType;
};


export type PanelPropsWithPanelType = PanelPropsByPanelType & {
    panelManagerConfig? :
        {
            openAsPanel : true;

            dontClearData? : boolean;
            condition? : Condition;

            openAsPopup? : never;
            isBlocking? : never;
            saveOnOk? : never;
        } |
        {
            openAsPopup : true;
            isBlocking? : boolean;
            saveOnOk? : boolean;

            dontClearData? : boolean;
            condition? : Condition;

            openAsPanel? : never;
        }
};

/**
 * @memberOf! Actions
 *
 * @param {PanelPropsWithPanelType} panelProps
 * @param {ProcessFinalPanelProps} [processFinalPanelProps]
 * @param {React.MouseEvent<HTMLElement, MouseEvent>} [event]                  The mouse event which triggered the action call if.
 * @param {boolean} [ignoreValidationErrors=false]
 * @param {number} [panelIndex]
 * @param {string} [path]
 *
 * @return {OpenPanelAction | SetPanelPropsValidationErrorAction}
 */
const openPanel = ( panelProps : PanelPropsWithPanelType, processFinalPanelProps? : ProcessFinalPanelProps, event? : React.MouseEvent<HTMLElement, MouseEvent> | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | KeyboardEvent, ignoreValidationErrors : boolean = false, panelIndex? : number, path? : string ) : OpenPanelAction | DenyOpenPanelAction | SetPanelPropsValidationErrorAction => {
    let retVal : OpenPanelAction | DenyOpenPanelAction | SetPanelPropsValidationErrorAction | undefined,
        panelPropsToUse = omniclone( panelProps ) as PanelPropsWithPanelType;

    const preValidation = preValidatePanelProps( panelPropsToUse );

    // is panelType set and instantiable
    if ( preValidation === true ) {
        const panelManagerConfig = panelPropsToUse.panelManagerConfig,
              newItemConfig      = panelPropsToUse.createNewItem;

        let schema : Schema | undefined,
            itemTypeId : number | undefined;

        delete panelPropsToUse.panelManagerConfig;
        delete panelPropsToUse.createNewItem;

        if (
            panelPropsToUse.panelType === 'MetaModelDesignerView'
        ) {
            delete panelPropsToUse.schema;
        }

        if (
            panelPropsToUse.panelType === 'Form'
            || panelPropsToUse.panelType === 'Overview'
            || panelPropsToUse.panelType === 'TreeView'
        ) {
            delete panelPropsToUse.viewName;
        }

        // should a new item be created
        // - validate itemType
        // - set panelProps id to 0 to get new panel
        if ( newItemConfig ) {
            schema = ( panelPropsToUse.schema ?? global.schema ) as Schema;
            itemTypeId = metaModel[ schema ].getItemType( newItemConfig.itemTypeId ?? newItemConfig.itemTypeName )?.id;

            if ( !itemTypeId ) {
                retVal = setPanelPropsValidationError( {
                    title  : __( 'YN.base.panelProps.createNewItem.error.title' ),
                    text   : __( 'YN.base.panelProps.createNewItem.error.text', { schema, itemTypeName: String( newItemConfig.itemTypeId ?? newItemConfig.itemTypeName ) } ),
                    details: [ __( 'YN.base.panelProps.createNewItem.itemTypeNameNotValid' ) ]
                } );
            }

            if ( newItemConfig.mapping?.id ) {
                // @ts-expect-error this is intentional
                panelPropsToUse[ newItemConfig.mapping?.id ] = 0;
            }
            else {
                panelPropsToUse.rootItemId = 0;
            }
        }
        else if (
            (
                panelPropsToUse.panelType === 'Form'
                || panelPropsToUse.panelType === 'TreeView'
                || panelPropsToUse.panelType === 'ViewDesigner'
            )
            && panelPropsToUse.rootItemId !== undefined
            && (
                panelPropsToUse.rootItemType !== undefined
                || panelPropsToUse.rootItemKey !== undefined
            )
        ) {
            if ( panelPropsToUse.rootItemId === 0 ) {
                delete panelPropsToUse.rootItemId;
            }
            else {
                delete panelPropsToUse.rootItemKey;
                delete panelPropsToUse.rootItemType;
            }
        }

        if ( !retVal ) {

            // pre-initialize by panel
            // @ts-expect-error - return of preInitializePanelProps extends PanelPropsWithPanelType
            panelPropsToUse = {
                panelType: panelPropsToUse.panelType,
                ...preInitializePanelProps( panelPropsToUse )
            };

            const PanelComponent = panelClasses[ panelPropsToUse.panelType as PanelType ] as unknown as PanelClass,
                  validation     = validatePanelProps( panelPropsToUse );

            // are panelProps valid
            if ( validation === true || ignoreValidationErrors ) {

                // get tab title for panel
                if ( !panelPropsToUse.panelTabTitle ) {
                    if ( ( panelPropsToUse.panelType === 'TreeView' || panelPropsToUse.panelType === 'ViewDesigner' ) && panelPropsToUse.labelKey ) {
                        panelPropsToUse.panelTabTitle = __( panelPropsToUse.labelKey );
                    }
                    else if ( PanelComponent.panelTabTitle ) {
                        panelPropsToUse.panelTabTitle = __( PanelComponent.panelTabTitle );
                    }
                    else {
                        panelPropsToUse.panelTabTitle = panelPropsToUse.panelType;
                    }
                }

                // get tab icon for panel
                if ( !panelPropsToUse.panelTabIcon ) {
                    if ( PanelComponent.panelTabIcon ) {
                        panelPropsToUse.panelTabIcon = resolveIconClass( PanelComponent.panelTabIcon );
                    }
                    else {
                        panelPropsToUse.panelTabIcon = '';
                    }
                }

                // initialize by panel
                // @ts-expect-error - return of initializePanelProps extends PanelPropsWithPanelType
                panelPropsToUse = {
                    panelType: panelPropsToUse.panelType,
                    ...initializePanelProps( panelPropsToUse )
                };

                let processFinalPanelPropsFnc : ProcessFinalPanelProps | undefined;

                // should a new item be created
                // - create item in finalize panelProps handler since the panelKey (mostly used as dataContext) is generated just before
                if ( newItemConfig ) {
                    processFinalPanelPropsFnc = ( finalizePanelProps ) => {
                        if ( processFinalPanelProps ) {
                            finalizePanelProps = processFinalPanelProps( finalizePanelProps );
                        }

                        // check for dataContext for all Panels even if not defined is intended
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        const newItem = new Item( { itemTypeId: itemTypeId! }, finalizePanelProps.dataContext ?? finalizePanelProps.uid ?? finalizePanelProps.panelKey, schema );

                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        if ( newItemConfig.key || newItemConfig.name || newItemConfig.statusId ) {
                            const itemUpdates : Partial<ItemOnlyAttributesForCreation> = {};

                            if ( newItemConfig.key ) {
                                itemUpdates.key = newItemConfig.key;
                            }
                            if ( newItemConfig.name ) {
                                itemUpdates.name = newItemConfig.name;
                            }
                            if ( newItemConfig.statusId ) {
                                itemUpdates.statusId = newItemConfig.statusId;
                            }

                            newItem.update( itemUpdates );
                        }

                        if ( newItemConfig.mapping?.id ) {
                            // @ts-expect-error this is intentional
                            finalizePanelProps[ newItemConfig.mapping?.id as keyof PanelPropsWithPanelType ] = newItem.id;
                        }
                        else {
                            // @ts-expect-error panels having a newItemConfig have alos a rootItemId prop
                            finalizePanelProps.rootItemId = newItem.id;
                        }

                        if ( newItemConfig.mapping ) {
                            for ( const attribute of Object.keys( newItemConfig.mapping ) ) {
                                if ( attribute === 'itemTypeName' ) {
                                    // @ts-expect-error this is intentional
                                    finalizePanelProps[ newItemConfig.mapping[ attribute ]! as keyof PanelPropsWithPanelType ] = newItem.itemType.name;
                                }
                                else if ( itemAttributes.includes( attribute as keyof ItemAttributes ) ) {
                                    // @ts-expect-error this is intentional
                                    finalizePanelProps[ newItemConfig.mapping[ attribute ]! as keyof PanelPropsWithPanelType ] = newItem[ attribute as keyof ItemAttributes ];
                                }
                            }
                        }

                        return finalizePanelProps;
                    };
                }

                // replace placeholders in panelPropsToUse
                try {
                    let panelPropsToUseForResolve = JSON.stringify( panelPropsToUse );

                    panelPropsToUseForResolve = messageUtils.getMessageTemplateStringResolverForData( {} )( panelPropsToUseForResolve );

                    const resolvedPanelProps = JSON.parse( panelPropsToUseForResolve );

                    // JSON serialization drops function-valued props (e.g. the onSave callback) -
                    // re-attach them from the pre-stringify panelProps so callbacks survive
                    for ( const key of Object.keys( panelPropsToUse ) ) {
                        if ( typeof panelPropsToUse[ key as keyof PanelPropsWithPanelType ] === 'function' ) {
                            resolvedPanelProps[ key ] = panelPropsToUse[ key as keyof PanelPropsWithPanelType ];
                        }
                    }

                    panelPropsToUse = resolvedPanelProps;
                }
                catch ( e ) {
                    console.error( e );
                }

                // open conditionally if
                if ( panelManagerConfig?.condition && !resolveConditionRecursive( panelManagerConfig?.condition, { user: new UserTemplateStringResolver( global.user ) } ) ) {
                    console.warn( `The panelConfig's condition is evaluated to false - so no panel or popup opened!`, panelProps );

                    retVal = {
                        type      : PANELMANAGER_OPEN_PANEL_DENIED,
                        panelProps: panelPropsToUse
                    };
                }

                // open as popup
                else if ( panelManagerConfig?.openAsPopup ) {
                    retVal = {
                        type                  : PANELMANAGER_OPEN_POPUP,
                        panelProps            : panelPropsToUse,
                        processFinalPanelProps: processFinalPanelPropsFnc ?? processFinalPanelProps,
                        isBlocking            : panelManagerConfig.isBlocking ?? false,
                        saveOnOk              : panelManagerConfig.saveOnOk ?? true,
                        dontClearData         : panelManagerConfig.dontClearData ?? false,
                        path
                    };
                }

                // open as normal panel
                else {
                    retVal = {
                        type                  : PANELMANAGER_OPEN_PANEL,
                        panelProps            : panelPropsToUse,
                        processFinalPanelProps: processFinalPanelPropsFnc ?? processFinalPanelProps,
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                        inBackground : ( event?.shiftKey && keyboardUtils.cmdKey( event ) ) || false,
                        panelIndex,
                        dontClearData: panelManagerConfig?.dontClearData ?? false,
                        path
                    };
                }
            }
            else {
                retVal = setPanelPropsValidationError( validation );
            }
        }
    }
    else {
        retVal = setPanelPropsValidationError( preValidation );
    }

    return retVal;
}
export type OpenPanel = typeof openPanel;

export default openPanel;
